import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../../stores/auth.store';
import { useModalStore } from '../../../stores/modal.store';
import { Icon, type IconName } from '../../../components/ui/Icon';
import { MobileSheet } from '../../../components/ui/MobileSheet';
import { SectionMark } from '../../../components/ui/SectionMark';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { SPESE_VISTE, type SpeseVista } from '../../../lib/adminRoutes';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur } from '../../../lib/utils';
import { canReadAdmin, canWriteAdmin } from '../adminApi';
import { AdminHeader, StoricoNote, useStorico, useVista } from '../shared/AdminShell';
import { daPagareQuery, giorniDaOggi, type DaPagare } from '../fatture/fattureApi';
import { SpeseDaPagare } from './SpeseDaPagare';
import { SpesePagate } from './SpesePagate';
import { SpeseMobile } from './SpeseMobile';
import { SpeseDaPagareOra, divideSpese } from './SpeseDaPagareOra';
import { SpeseDaRegistrare } from './SpeseDaRegistrare';
import { SpeseFisseLoghi, useSpeseFisse } from './SpeseFisseLoghi';
import { useIsMobile } from '../../../hooks/useIsMobile';
import '../admin.css';
import '../fatture/fatture.css';
import './spese.css';

/**
 * Spese (`/admin/spese`), ridisegnata il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: cosa paghiamo ogni mese e a chi, e cosa c'è da pagare adesso?
 * La forma: una pagina sola. In cima la risposta in una frase; poi "Da pagare" (quello per cui
 * serve un bonifico nostro), gli addebiti automatici passati da spuntare (chiusi in una riga),
 * le fatture arrivate su FattureInCloud e ancora da registrare (dal 27/10, chiuse in una riga
 * anche loro: SpeseDaRegistrare) e le spese fisse coi loghi dei fornitori per tipo, ognuno col
 * totale del mese.
 *
 * Alleggerita ("troppe cose inutili", Lorenzo 26/10): via le tre linguette, la fascia dei tre
 * numeri, ricerca, ordina, filtra, "per data / per fornitore", il sottotitolo coi link a Fisco
 * e Dipendenti. Restano raggiungibili dal menu ⋯, con lo stesso indirizzo di prima:
 *   Spese già pagate                         ?vista=pagate  (SpesePagate, com'era)
 *   Fatture dei fornitori tolte dall'elenco  ?status=tolte  (com'era)
 * `?vista=fisse` (link vecchi) apre la pagina principale: le spese fisse adesso sono lì.
 * Sul telefono la pagina è SpeseMobile.
 */

/**
 * Sul telefono la pagina è SpeseMobile (la risposta in cima, righe al posto di
 * schede, i gesti nel pannello della riga); "Nuova spesa" resta in fondo, con
 * le stesse tre scelte di qui. Sul computer resta questa.
 */
export function SpesePage() {
  const isMobile = useIsMobile();
  return isMobile ? <SpeseMobile nuovaSpesa={<NuovaSpesaMobile />} /> : <SpeseDesktop />;
}

function SpeseDesktop() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const [vista, setVista] = useVista<SpeseVista>(SPESE_VISTE);
  const [status, setStatus] = useUrlState<string>('status', '');
  const storico = useStorico();
  const lettore = canReadAdmin(user);
  const { data } = useQuery({ ...daPagareQuery(storico.on), enabled: lettore });
  const fisse = useSpeseFisse();

  // Senza l'Amministrazione ogni chiamata darebbe 403: si dice subito, una volta sola.
  if (!lettore) {
    return (
      <div className="page adm-page">
        <AdminHeader title="Spese" />
        <EmptyState brand title="Le spese non sono per il tuo profilo" description="Per vederle serve l'accesso all'Amministrazione: chiedilo a chi gestisce gli utenti." />
      </div>
    );
  }

  const pagate = vista === 'pagate';
  const tolte = !pagate && status === 'tolte';
  const torna = () => { setStatus(''); setVista('dapagare'); };
  const menu: ActionItem[] = [
    { key: 'pagate', label: 'Le spese già pagate', icon: 'check', onClick: () => setVista('pagate'), hidden: pagate },
    { key: 'tolte', label: "Le fatture dei fornitori tolte dall'elenco", icon: 'trash', onClick: () => { setVista('dapagare'); setStatus('tolte'); }, hidden: tolte },
    { ...storico.menuItem },
  ];

  return (
    <div className="page adm-page ft-page">
      <header className="ft-hd">
        <div className="ft-hd-main">
          <SectionMark icon="receipt" className="ft-sez" />
          <div className="min-w-0">
            <h1 className="ft-title">Spese</h1>
            {pagate || tolte ? (
              <p className="ft-risposta">
                <a className="ft-indietro" onClick={torna}><Icon name="arrowLeft" size={12} /> Cosa c'è da pagare</a>
                <span className="ft-dove">{pagate ? 'Le spese già pagate' : "Le fatture dei fornitori tolte dall'elenco"}</span>
              </p>
            ) : (
              <p className="ft-risposta">{data && fisse.data ? risposta(data, fisse.data.totals.monthly, fisse.fornitori) : 'Sto guardando le spese…'}</p>
            )}
          </div>
        </div>
        <div className="ft-hd-azioni">
          {canWrite && <NuovaSpesaButton />}
          <ActionsMenu variant="ghost" label="Altre liste di spese" items={menu} />
        </div>
      </header>

      {storico.on && <StoricoNote year={storico.year} onOff={() => storico.set(false)} />}

      {pagate ? <SpesePagate showHistory={storico.on} tabs={null} />
        : tolte ? <SpeseDaPagare canWrite={canWrite} showHistory={storico.on} onPagate={() => setVista('pagate')} tabs={null} />
        : (
          <>
            {data ? <SpeseDaPagareOra data={data} canWrite={canWrite} /> : <p className="ft-vuoto">Carico le spese da pagare…</p>}
            {/* Le fatture arrivate su FattureInCloud e non ancora registrate (27/10/2026): non sono
                ancora spese, ma lo diventano appena registrate. Senza niente da registrare non c'è. */}
            <SpeseDaRegistrare />
            <SpeseFisseLoghi />
          </>
        )}

      {/* Sul telefono l'azione principale sta in fondo (mobile.md §4) e le
          scelte salgono dal basso (§6). */}
      {canWrite && (
        <div className="m-actionbar m-only">
          <NuovaSpesaMobile />
        </div>
      )}
    </div>
  );
}

/** La risposta in una frase: quanto costa un mese e cosa c'è da pagare a mano. Ogni numero una volta. */
function risposta(d: DaPagare, alMese: number, fornitori: number): ReactNode {
  const { daPagare, daSpuntare } = divideSpese(d);
  const totale = daPagare.reduce((s, r) => s + r.amount, 0);
  const scadute = daPagare.filter((r) => (giorniDaOggi(r.dueDate) ?? 0) < 0).length;
  return (
    <>
      Ogni mese paghiamo <strong>€ {fmtEur(alMese)}</strong> di spese fisse a {fornitori} fornitori, quasi tutte con addebito automatico.{' '}
      {daPagare.length > 0 ? (
        <>Da pagare con un bonifico nostro ci {daPagare.length === 1 ? 'è una spesa' : `sono ${daPagare.length} spese`}, <strong>€ {fmtEur(totale)}</strong>
          {scadute > 0 ? <>: <span className="is-alert">{scadute === 1 ? 'una è già scaduta' : `${scadute} già scadute`}</span>.</> : '.'}</>
      ) : <>Niente da pagare a mano nei prossimi 30 giorni.</>}
      {daSpuntare.length > 0 && <> {daSpuntare.length} addebiti automatici già passati sono da spuntare.</>}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// "+ Nuova spesa": tre voci
// ════════════════════════════════════════════════════════════════════

type Tipo = 'newPayable' | 'newRecurring' | 'newCashEntry';
const SCELTE: Array<{ kind: Tipo; icon: IconName; title: string; sub: string; payload?: Record<string, unknown> }> = [
  { kind: 'newPayable', icon: 'receipt', title: 'Fattura di un fornitore', sub: 'Una fattura ricevuta che non è ancora su FattureInCloud' },
  { kind: 'newRecurring', icon: 'repeat', title: 'Spesa fissa', sub: 'Affitto, utenze, abbonamenti, rate' },
  { kind: 'newCashEntry', icon: 'banknote', title: 'Uscita in contanti', sub: 'Pulizie, manutenzioni, piccole spese', payload: { direction: 'OUT' } },
];

function NuovaSpesaButton() {
  const [open, setOpen] = useState(false);
  const openModal = useModalStore((s) => s.open);
  return (
    <div className="relative">
      <button className="btn btn-primary" onClick={() => setOpen((v) => !v)}>
        <Icon name="plus" /> Nuova spesa <Icon name="chevronD" size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[61] rounded-[var(--r-card)] border border-line bg-white shadow-lg overflow-hidden p-2" style={{ minWidth: 340 }}>
            {SCELTE.map((s) => (
              <Choice key={s.kind} icon={s.icon} title={s.title} sub={s.sub} onClick={() => { setOpen(false); openModal(s.kind, s.payload); }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NuovaSpesaMobile() {
  const [open, setOpen] = useState(false);
  const openModal = useModalStore((s) => s.open);
  return (
    <>
      <button className="btn btn-primary" onClick={() => setOpen(true)}>
        <Icon name="plus" size={12} /> Nuova spesa
      </button>
      <MobileSheet open={open} onClose={() => setOpen(false)} title="Che spesa vuoi registrare?">
        {SCELTE.map((s) => (
          <Choice key={s.kind} icon={s.icon} title={s.title} sub={s.sub} onClick={() => { setOpen(false); openModal(s.kind, s.payload); }} />
        ))}
      </MobileSheet>
    </>
  );
}

function Choice({ icon, title, sub, onClick }: { icon: IconName; title: string; sub: string; onClick: () => void }) {
  return (
    <div className="flex items-center gap-2.5 p-2.5 hover:bg-paper-2 cursor-pointer rounded-[var(--r-ctl)] transition" role="button" tabIndex={0}
      onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
      <div className="icon-tile sm"><Icon name={icon} size={16} /></div>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="mute text-[12px]">{sub}</div>
      </div>
      <Icon name="arrowRight" size={12} className="mute" />
    </div>
  );
}
